import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography, IconButton, Tooltip } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useTranslation } from 'react-i18next';
import { PmCard, PmPill } from '../../components/pm';
import type { AmpelBuckets } from './ampel';

dayjs.extend(isoWeek);

export interface TimelineProject {
  id: string;
  name: string;
  nickname?: string | null;
  planned_start: string;
  planned_end: string;
  state: string;
  /** Ampel-Verteilung der 17 Schritte; bestimmt den Segment-Verlauf des Balkens.
   *  Fehlt, solange keine Reifegrad-Daten vorliegen — dann neutraler Balken. */
  buckets?: AmpelBuckets;
}

export interface TimelineMeeting {
  id: string;
  titel: string;
  datum: string;
  uhrzeit?: string | null;
}

interface ProjectTimelineGanttProps {
  projects: TimelineProject[];
  onSelect: (id: string) => void;
  meetings?: TimelineMeeting[];
  onMeetingSelect?: (meeting: TimelineMeeting, anchor: HTMLElement) => void;
}

type Zoom = 'week' | 'month';

const LEFT_PAD = 200;
const HEADER_H = 48;
const ROW_H = 34;
const MEETING_BAND = 34;

interface RangeCalc {
  start: Dayjs;
  end: Dayjs;
  cellW: number;
  totalDays: number;
}

function projectLabel(p: TimelineProject): string {
  const nick = p.nickname?.trim();
  return nick && nick.length > 0 ? nick : p.name;
}

function dayToX(date: string, range: RangeCalc): number {
  return dayjs(date).diff(range.start, 'day') * range.cellW;
}

/**
 * Projektübergreifende Gantt-Timeline — SVG-basiert, ein Balken pro Projekt
 * von planned_start bis planned_end. Klick auf einen Balken öffnet das
 * Projekt-Dashboard. Globale Meetings erscheinen als leichte Vertikal-Linie
 * mit Gruppen-Icon unter der Achse (Klick öffnet Detail-Popover). KW ist der
 * Default-Zoom; der 'Jetzt'-Marker wird beim Öffnen automatisch sichtbar.
 */
export function ProjectTimelineGantt({ projects, onSelect, meetings = [], onMeetingSelect }: ProjectTimelineGanttProps) {
  const { t } = useTranslation('projects');
  const [zoom, setZoom] = useState<Zoom>('week');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Projekte chronologisch nach Start sortieren.
  const rows = useMemo(
    () => [...projects].sort((a, b) => a.planned_start.localeCompare(b.planned_start)),
    [projects],
  );

  const range: RangeCalc = useMemo(() => {
    const dates: string[] = [];
    rows.forEach((p) => { dates.push(p.planned_start); dates.push(p.planned_end); });
    meetings.forEach((m) => { if (m.datum) dates.push(m.datum); });
    let start = dayjs().startOf('month');
    let end = dayjs().add(3, 'month').endOf('month');
    if (dates.length > 0) {
      const sorted = [...dates].sort();
      start = dayjs(sorted[0]).startOf('week').subtract(7, 'day');
      end = dayjs(sorted[sorted.length - 1]).endOf('week').add(7, 'day');
    }
    const totalDays = end.diff(start, 'day') + 1;
    const cellW = zoom === 'week' ? 14 : 5;
    return { start, end, cellW, totalDays };
  }, [rows, meetings, zoom]);

  const W = Math.max(800, LEFT_PAD + range.totalDays * range.cellW + 40);
  const rowsAreaH = rows.length * ROW_H + 16;
  const hasMeetings = meetings.length > 0;
  const bandTop = HEADER_H + rowsAreaH;
  const H = bandTop + (hasMeetings ? MEETING_BAND : 0);

  const headers = useMemo(() => {
    const out: Array<{ x: number; label: string; isHighlight: boolean }> = [];
    let cursor = range.start.clone();
    const now = dayjs();
    while (cursor.isBefore(range.end)) {
      const x = LEFT_PAD + cursor.diff(range.start, 'day') * range.cellW;
      const label = zoom === 'month' ? cursor.format('MMM YY') : `KW ${cursor.isoWeek()}`;
      const isHighlight = zoom === 'month'
        ? cursor.month() === now.month() && cursor.year() === now.year()
        : cursor.isoWeek() === now.isoWeek() && cursor.year() === now.year();
      out.push({ x, label, isHighlight });
      cursor = zoom === 'month' ? cursor.add(1, 'month').startOf('month') : cursor.add(7, 'day');
    }
    return out;
  }, [range, zoom]);

  const todayX = useMemo(() => {
    const now = dayjs();
    if (now.isBefore(range.start) || now.isAfter(range.end)) return null;
    return LEFT_PAD + now.diff(range.start, 'day') * range.cellW;
  }, [range]);

  // Auto-Scroll: 'Jetzt' beim Öffnen und bei Zoom-Wechsel direkt sichtbar
  // machen — leicht links positioniert (knapp rechts der Projekt-Label-Spalte),
  // damit kommende Termine im Fokus stehen und die Labels sichtbar bleiben.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || todayX == null) return;
    el.scrollLeft = Math.max(0, todayX - LEFT_PAD - 40);
  }, [todayX, zoom, rows.length]);

  const zoomBtnSx = { border: 0, background: 'none', color: 'inherit', cursor: 'pointer', font: 'inherit' } as const;

  return (
    <PmCard>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('timeline.ganttTitle', 'Projekt-Timeline')}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
            {t('timeline.zoomLabel', 'Ansicht')}
          </Typography>
          <PmPill tone={zoom === 'week' ? 'primary' : 'neutral'}>
            <Box component="button" onClick={() => setZoom('week')} sx={zoomBtnSx}>
              {t('timeline.zoomWeekShort', 'KW')}
            </Box>
          </PmPill>
          <PmPill tone={zoom === 'month' ? 'primary' : 'neutral'}>
            <Box component="button" onClick={() => setZoom('month')} sx={zoomBtnSx}>
              {t('timeline.zoomMonthShort', 'Monat')}
            </Box>
          </PmPill>
        </Stack>
      </Stack>

      {/* Legende: Ampel-Segmente nach Schritt-Reifegrad */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 1.5, flexWrap: 'wrap', fontSize: '0.72rem', color: 'var(--pm-fg-muted)' }}
      >
        {[
          { c: 'var(--pm-success)', label: t('timeline.legendDone', 'abgeschlossen (≥4)') },
          { c: 'var(--pm-warning)', label: t('timeline.legendWip', 'in Arbeit (2–3)') },
          { c: 'var(--pm-error)', label: t('timeline.legendOpen', 'offen (0–1)') },
          { c: 'var(--pm-bg-subtle)', label: t('timeline.legendIdle', 'Phase nicht erreicht') },
        ].map((it) => (
          <Box key={it.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: it.c, border: '1px solid var(--pm-bg-muted)' }} />
            {it.label}
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <GroupsIcon sx={{ fontSize: 14, color: 'var(--pm-info)' }} />
          {t('timeline.legendMeeting', 'globales Meeting')}
        </Box>
      </Stack>

      <Box ref={scrollRef} sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ position: 'relative', width: W }}>
          <svg width={W} height={H} style={{ minWidth: 800, userSelect: 'none', display: 'block' }}>
            {/* Header: Projekt-Spalte */}
            <rect x={0} y={0} width={LEFT_PAD} height={HEADER_H} fill="var(--pm-bg-deep)" />
            <text x={12} y={HEADER_H / 2 + 4} fontSize="11" fill="var(--pm-fg-subtle)" fontWeight={700} letterSpacing="0.08em">
              {t('timeline.colProject', 'PROJEKT')}
            </text>

            {/* Header: Zeitskala */}
            <rect x={LEFT_PAD} y={0} width={W - LEFT_PAD} height={HEADER_H} fill="var(--pm-bg-deep)" />
            {headers.map((h, i) => (
              <g key={i}>
                <line x1={h.x} y1={0} x2={h.x} y2={H} stroke="var(--pm-bg-muted)" strokeWidth={0.5} />
                <text x={h.x + 4} y={HEADER_H - 14} fontSize="10" fill={h.isHighlight ? 'var(--pm-warning)' : 'var(--pm-fg-muted)'} fontWeight={h.isHighlight ? 700 : 500}>
                  {h.label}
                </text>
              </g>
            ))}

            {/* Globale Meetings: leichte Vertikal-Linie ueber die volle Hoehe */}
            {meetings.map((m) => {
              if (!m.datum) return null;
              const x = LEFT_PAD + dayToX(m.datum, range);
              return (
                <line
                  key={`ml-${m.id}`}
                  x1={x}
                  y1={HEADER_H}
                  x2={x}
                  y2={H}
                  stroke="var(--pm-info)"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  opacity={0.4}
                />
              );
            })}

            {/* Aktuelle Zeit als gelbe Vertikal-Linie */}
            {todayX != null && (
              <>
                <line x1={todayX} y1={HEADER_H} x2={todayX} y2={H} stroke="var(--pm-warning)" strokeWidth={2} opacity={0.7} />
                <circle cx={todayX} cy={HEADER_H - 6} r={4} fill="var(--pm-warning)" />
              </>
            )}

            {/* Zeilen-Hintergrund (Zebra) */}
            {rows.map((_, i) => (
              <rect
                key={i}
                x={0}
                y={HEADER_H + i * ROW_H}
                width={W}
                height={ROW_H}
                fill={i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'}
              />
            ))}

            {/* Projekt-Zeilen: Label links + Segment-Balken nach Ampel-Verteilung */}
            {rows.map((p, i) => {
              const x1 = dayToX(p.planned_start, range);
              const x2 = dayToX(p.planned_end, range);
              const xStart = LEFT_PAD + x1;
              const width = Math.max(8, x2 - x1);
              const y = HEADER_H + i * ROW_H + 7;
              const h = ROW_H - 14;
              const label = projectLabel(p);
              const b = p.buckets;

              // Segmente proportional zur Ampel-Verteilung (Reihenfolge rot->gelb->gruen),
              // idle bleibt als neutraler Rest hinter dem Balken-Hintergrund sichtbar.
              const segRects: Array<{ x: number; w: number; fill: string }> = [];
              if (b && b.total > 0) {
                const defs = [
                  { n: b.open, fill: 'var(--pm-error)' },
                  { n: b.wip, fill: 'var(--pm-warning)' },
                  { n: b.done, fill: 'var(--pm-success)' },
                ];
                let acc = xStart;
                for (const d of defs) {
                  const w = width * (d.n / b.total);
                  if (w > 0.5) segRects.push({ x: acc, w, fill: d.fill });
                  acc += w;
                }
              }
              const health = b ? `\n${b.done} grün · ${b.wip} gelb · ${b.open} rot · ${b.idle} offen` : '';
              const tooltip = `${label}${p.nickname ? ` (${p.name})` : ''}\n${t(`states.${p.state}`, p.state)}\n${dayjs(p.planned_start).format('DD.MM.YYYY')} – ${dayjs(p.planned_end).format('DD.MM.YYYY')}${health}`;
              return (
                <g
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelect(p.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(p.id); }}
                >
                  <title>{tooltip}</title>
                  {/* Klick-Fläche über die ganze Zeile */}
                  <rect x={0} y={HEADER_H + i * ROW_H} width={W} height={ROW_H} fill="transparent" />
                  <text
                    x={12}
                    y={HEADER_H + i * ROW_H + ROW_H / 2 + 4}
                    fontSize="11"
                    fill="var(--pm-fg-base)"
                    fontWeight={600}
                  >
                    {label.slice(0, 26)}{label.length > 26 ? '…' : ''}
                  </text>
                  {/* Balken-Hintergrund über die volle Zeitspanne (neutraler idle-Rest) */}
                  <rect
                    x={xStart}
                    y={y}
                    width={width}
                    height={h}
                    rx={4}
                    fill={b ? 'var(--pm-bg-subtle)' : 'var(--pm-gray-400)'}
                    opacity={b ? 1 : 0.5}
                    stroke="var(--pm-bg-muted)"
                    strokeWidth={1}
                  />
                  {/* Ampel-Segmente */}
                  {segRects.map((s, si) => (
                    <rect key={si} x={s.x} y={y} width={s.w} height={h} fill={s.fill} opacity={0.9} />
                  ))}
                  {/* Projektname auf dem Balken — weisse Schrift mit dunklem Halo
                      (paint-order=stroke), damit sie ueber jedem Segment UND dem
                      dunklen Hintergrund lesbar bleibt, falls sie ueberlaeuft. */}
                  <text
                    x={xStart + 8}
                    y={y + h / 2 + 4}
                    fontSize="11"
                    fontWeight={700}
                    fill="#ffffff"
                    stroke="rgba(0,0,0,0.72)"
                    strokeWidth={3}
                    paintOrder="stroke"
                    pointerEvents="none"
                  >
                    {label.slice(0, 40)}{label.length > 40 ? '…' : ''}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Meeting-Icons als HTML-Overlay (Popover-Anker) im Band unter der Achse */}
          {meetings.map((m) => {
            if (!m.datum) return null;
            const x = LEFT_PAD + dayToX(m.datum, range);
            const time = m.uhrzeit ? ` ${m.uhrzeit}` : '';
            return (
              <Tooltip key={`mi-${m.id}`} title={`${m.titel} — ${dayjs(m.datum).format('DD.MM.YYYY')}${time}`} arrow>
                <IconButton
                  size="small"
                  aria-label={m.titel}
                  onClick={(e) => onMeetingSelect?.(m, e.currentTarget)}
                  sx={{
                    position: 'absolute',
                    left: x - 14,
                    top: bandTop + 2,
                    width: 28,
                    height: 28,
                    color: 'var(--pm-info)',
                    bgcolor: 'var(--pm-bg-deep)',
                    border: '1px solid var(--pm-info)',
                    '&:hover': { bgcolor: 'var(--pm-bg-subtle)' },
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </PmCard>
  );
}
